import { useContext, useEffect, useLayoutEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { createPortal } from "react-dom";
import { CardBody, CardHeader } from "reactstrap";
import Form from "./Form";
import NTable from "../../../components/NTable";
import Drawer from "../../../components/Drawer";
import Modal from "../../../components/Modal";
import Button from "../../../components/Button";
import { useDeleteModal } from "../../Settings/helpers/useDeleteModal";
import { useNewTable } from "../../../components/NTable/hook";
import { useDrawerProps } from "../../Settings/helpers/useDrawerProps";
import { useColumns } from "./columns";
import { ProfileContext } from "../context";
import { useRequestTimeOffApi } from "./api";
import { useCompanyDateFormat } from "../../../utils/hooks/company";
import { differenceFromNow, dateTimeFormat } from "../../../utils/helpers/date";
import { useVisible } from "../../../utils/hooks/useVisible";
import { useAccess, useActionWithAccess } from "../../../utils/hooks/access";
import InfoModal from "./InfoModal";
import PendingAdjustmentModal from "./PendingAdjustment";
import ScheduledChanges from "./ScheduledChanges";
import LedgerContent from "../History/Timeoff/LedgerContent";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faXmark } from "@fortawesome/free-solid-svg-icons";

const useCustomDrawer = (getChildren) => {
	const [wrapperElement, setWrapperElement] = useState(null);

	useLayoutEffect(() => {
		const element = document.getElementById("timeoff-drawer");
		setWrapperElement(element);
	}, [setWrapperElement]);

	if (wrapperElement === null) return null;

	return createPortal(getChildren(), wrapperElement);
};

function TimeOff({ isOpenFromTimesheet }) {
	const { t } = useTranslation();
	const { user, isSelf } = useContext(ProfileContext);
	// same permission as the History | Ledger toggle in Profile > History > Time off
	const { hasAccess: canViewLedger } = useAccess(
		isSelf ? "profile.viewTimeOffHistory" : "people.viewTimeOffHistory",
	);
	// scheduled plan changes are made and listed with the same permission as effective-dated ones
	const { hasAccess: canManagePlans } = useAccess("profile.canAddTeamPlan");
	const dateFormat = useCompanyDateFormat();
	const { visible, open, close, selected: selectedPendingItems } = useVisible();

	const {
		state: { mode, data, totalItems, loading, submitting, error },
		selected,
		fetch,
		create,
		update,
		goToView,
		goToCreate,
		goToEdit,
		goToClone,
		remove,
		goToTransfer,
		goToLedger,
		transfer,
		effective,
		scheduled,
		fetchScheduled,
		schedule,
		cancelScheduled,
		goToPayout,
		goToPayoutAll,
		previewPayout,
		payout,
	} = useRequestTimeOffApi();
	// the pending adjustment whose details window is open: { planId, adjustmentId }
	const [reviewing, setReviewing] = useState(null);

	const { modal: deleteModal, open: openDeleteModal } = useDeleteModal({
		message: () => t("do-you-want-to-delete-plan"),
		onConfirm: (data) => remove(data),
		permission: "canManageUsers",
	});

	const payoutPermission = isSelf ? "profile.canPayoutTimeoff" : "people.canPayoutTimeoff";
	const goToPayoutWithAccess = useActionWithAccess(goToPayout, payoutPermission);
	const { hasAccess: canPayout } = useAccess(payoutPermission);
	const hasActivePlan = (data || []).some((plan) => plan.status === "active");

	const columns = useColumns({
		edit: goToEdit,
		remove: openDeleteModal,
		clone: goToClone,
		isOpenFromTimesheet,
		transfer: goToTransfer,
		ledger: canViewLedger ? goToLedger : undefined,
		open,
		reviewAdjustment: (planId, adjustmentId) => setReviewing({ planId, adjustmentId }),
		scheduled,
		payout: goToPayoutWithAccess,
	});
	const table = useNewTable({
		data,
		totalItems,
		loading,
		columns,
		enableRowSelection: false,
	});

	const { open: drawerOpen, title: modeTitle } = useDrawerProps({
		mode,
		create: t("add-new-plan"),
		edit: t("edit-plan"),
		clone: t("clone-plan"),
		transfer: t("transfer-plan"),
		payout: t("payout-plan"),
	});
	const drawerTitle = mode === "payout-all" ? t("payout-all-plans") : modeTitle;

	const drawer = useCustomDrawer(() => {
		if (mode === "ledger") return null;

		return (
			<Drawer
				title={drawerTitle}
				open={drawerOpen}
				close={goToView}
				style={{
					maxWidth: "initial",
				}}
			>
				<Form
					mode={mode}
					error={error}
					loading={submitting}
					close={goToView}
					create={create}
					update={update}
					transfer={transfer}
					effective={effective}
					schedule={canManagePlans ? schedule : undefined}
					payout={payout}
					previewPayout={previewPayout}
					allPlans={data}
					values={selected}
				/>
			</Drawer>
		);
	});

	useEffect(() => {
		if (!user) return;
		const controller = new AbortController();
		fetch(controller);
		return () => controller.abort();
	}, [fetch, user]);

	useEffect(() => {
		if (!user || !canManagePlans || isOpenFromTimesheet) return;
		const controller = new AbortController();
		fetchScheduled(controller);
		return () => controller.abort();
	}, [fetchScheduled, user, canManagePlans, isOpenFromTimesheet]);

	return (
		<>
			{!isOpenFromTimesheet && (
				<CardHeader className="border-0">
					<div className="d-flex justify-content-between">
						<div className="d-flex flex-column">
							<h3>
								{t("length-of-service")}:{" "}
								{user?.hiringDate &&
									dateTimeFormat(user?.hiringDate, dateFormat)}
							</h3>

							<p>
								{user?.hiringDate &&
									differenceFromNow(user?.hiringDate)}
							</p>
						</div>

						<div className="d-flex align-items-start">
							{canPayout && hasActivePlan && (
								<Button
									onClick={goToPayoutAll}
									className="border-dark pr-3 mr-2 btn-round btn-icon shadow-none btn btn-none btn-sm"
								>
									{t("payout-all-plans")}
								</Button>
							)}
							<Button
								onClick={goToCreate}
								className="border-dark pr-3 btn-round btn-icon shadow-none btn btn-none btn-sm"
							>
								{t("assign-plan")}
							</Button>
						</div>
					</div>
				</CardHeader>
			)}

			<CardBody
				className="p-0"
				style={{
					height: "calc(100% - 20px)",
					overflowY: "auto",
				}}
			>
				{!isOpenFromTimesheet && <ScheduledChanges changes={scheduled} cancel={cancelScheduled} />}
				<NTable table={table} />
			</CardBody>

			{drawer}

			{/* the 5-column ledger table is not readable in a drawer */}
			<Modal
				isOpen={mode === "ledger" && !!selected}
				toggle={goToView}
				size="lg"
				scrollable
				className="ledger-modal"
				title={(
					<div className="d-flex align-items-center justify-content-between gap-2">
						<span>{`${t("ledger")}${selected?.plan?.code ? `: ${selected.plan.code}` : ""}`}</span>
						<FontAwesomeIcon className="cursor-pointer" icon={faXmark} onClick={goToView} />
					</div>
				)}
			>
				{mode === "ledger" && selected && (
					<LedgerContent key={selected.id} plan={selected} asPanel={false} />
				)}
			</Modal>
			<PendingAdjustmentModal
				userId={user?.id}
				planId={reviewing?.planId}
				adjustmentId={reviewing?.adjustmentId}
				isOpen={!!reviewing}
				close={() => setReviewing(null)}
				onDecided={() => fetch()}
			/>
			<InfoModal
				values={selectedPendingItems}
				isOpen={visible}
				close={close}
			/>
			{deleteModal}
		</>
	);
}

export default TimeOff;
